import { useCurrentShop } from '#storefront/composables'
import { getDeepestCategoryForTracking } from '~/utils'
import type {
  TrackingEvent,
  TrackingPayload,
  ProductViewData,
  TrackSelectItemEventParams,
  TrackViewItemParams,
  TrackViewItemListEventParams,
  TrackProductSizeSelectionParams,
  BasicViewData,
} from '~/types/tracking'
import { useTracking } from '~/composables/useTracking'
import { useProductClickStorage } from '~/composables/useProductClickStorage'

const useProductEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => void,
) => {
  const currencyCode = useCurrentShop().value!.currency
  const { flush } = useTracking()
  const { getProductClick } = useProductClickStorage()

  return {
    trackSelectItem: ({
      product,
      category,
      listingMetaData,
      index = -1, // index starts from 1
      variant,
      quantity = 1,
      pagePayload,
    }: TrackSelectItemEventParams) => {
      // Check local storage for stored product click data
      const storedProductClick = getProductClick(product.id)

      // Use stored list data if available, otherwise fall back to passed listingMetaData
      let listData
      if (storedProductClick) {
        listData = {
          name: storedProductClick.item_list_name,
          id: storedProductClick.item_list_id,
          index: index > -1 ? index : undefined,
        }
      } else if (listingMetaData) {
        listData = {
          name: listingMetaData.name,
          id: listingMetaData.id,
          index: index > -1 ? index : undefined,
        }
      } else {
        listData = undefined
      }

      const payload = {
        product,
        quantity,
        category: category
          ? {
              name: category?.name || '',
              id: category?.id?.toString() || '',
            }
          : undefined,
        list: listData,
        variant: variant ?? undefined,
        pagePayload: pagePayload,
        // Include pagination parameters for enhanced analytics
        totalPages: pagePayload?.totalPages || undefined,
        pageSize: pagePayload?.pageSize || undefined,
        currentPage: pagePayload?.currentPage || undefined,
        resultsCount: pagePayload?.resultsCount || undefined,
      }

      track('select_item', {
        ...payload,
        currencyCode,
      })
    },
    trackViewItem: ({
      product,
      quantity = 1,
      variant,
    }: TrackViewItemParams) => {
      // Check local storage for stored product click data
      const storedProductClick = getProductClick(product.id)

      // Use stored list data if available, otherwise fall back to empty values
      const listData = storedProductClick
        ? {
            index: 1,
            name: storedProductClick.item_list_name,
            id: storedProductClick.item_list_id,
          }
        : {
            index: 1,
            name: '',
            id: '',
          }

      track('view_item', {
        product,
        quantity,
        variant,
        list: listData,
        currencyCode,
      })
    },
    trackViewItemList: ({
      items,
      listingMetaData,
      paginationOffset = 0,
      category,
      positionOffset = -1,
      pagePayload,
    }: TrackViewItemListEventParams) => {
      const itemsToTrack = items?.map(
        (product): ProductViewData => ({
          product,
          category: category || {
            name: getDeepestCategoryForTracking(product.categories).name,
            id: getDeepestCategoryForTracking(product.categories).id,
          },
          list: {
            name: listingMetaData.name,
            id: listingMetaData.id,
            index: paginationOffset + (product.index + 1),
          },
          position:
            positionOffset > -1
              ? positionOffset + product.index + 1
              : undefined,
        }),
      )

      track('view_item_list', {
        items: itemsToTrack,
        currencyCode,
        pagePayload: {
          action: 'view list',
          ...(pagePayload as BasicViewData),
        },
      })
    },
    trackProductSizeSelection: ({
      action,
      label,
      itemId,
    }: TrackProductSizeSelectionParams) => {
      track('product_size_selection', {
        category: 'product',
        action,
        label,
        item_id: itemId,
      })

      flush()
    },
  }
}

export default useProductEvents
